import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import IssueReportForm from '../IssueReport/IssueReportForm';
import { useMercenary } from '../../contexts/MercenaryContext';
import styles from './Header.module.css';

interface User {
  role: {
    name: string;
  };
}

interface SeasonInfo {
  id: number;
  name: string;
  seasonNumber: number;
  currentDayNumber: number | null;
  status: string;
  totalDays?: number; // Total duration in days (e.g., 59-62 for normal season)
}

interface ScheduleEvent {
  id: number;
  eventType: string;
  eventName: string;
  scheduledDate: string;
  status: string;
}

interface HeaderProps {
  user: User | null;
  pendingFeedback?: { formId: number; title: string } | null;
  onOpenFeedback?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, pendingFeedback, onOpenFeedback }) => {
  const { mercenaries, mercenaryId, switchMercenary } = useMercenary();
  const location = useLocation();
  const navigate = useNavigate();
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [onlineMercenaries, setOnlineMercenaries] = useState<number>(0);
  const [users24h, setUsers24h] = useState<number>(0);
  const [usersSeason, setUsersSeason] = useState<number>(0);
  const [battlesToday, setBattlesToday] = useState<number>(0);
  const [battlesWeek, setBattlesWeek] = useState<number>(0);
  const [battlesSeason, setBattlesSeason] = useState<number>(0);
  const [seasonInfo, setSeasonInfo] = useState<SeasonInfo | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleEvents, setScheduleEvents] = useState<ScheduleEvent[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [localTime, setLocalTime] = useState(new Date());
  const [serverTimeOffset, setServerTimeOffset] = useState<number>(0);

  const fetchScheduleEvents = useCallback(async (seasonId: number) => {
    setScheduleLoading(true);
    try {
      const res = await axios.get(`/api/seasons/${seasonId}/schedule`, { withCredentials: true });
      const upcoming = (res.data.events || []).filter(
        (e: ScheduleEvent) => e.status === 'scheduled' || e.status === 'in_progress'
      );
      setScheduleEvents(upcoming);
    } catch {
      setScheduleEvents([]);
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  const handleSeasonCardClick = () => {
    if (!seasonInfo) return;
    setShowScheduleModal(true);
    fetchScheduleEvents(seasonInfo.id);
  };

  // Quick-swap with team-battle and beast-battle redirect awareness
  const handleQuickSwitch = useCallback(async (m: any) => {
    if (m.id === mercenaryId) return;
    await switchMercenary(m);
    if (location.pathname.startsWith('/team-battle') || location.pathname.startsWith('/ranked-battle')) {
      try {
        const res = await axios.get('/api/team-battle/participation', { withCredentials: true });
        const isOnRankedPath = location.pathname.startsWith('/ranked-battle');
        // Use the type that matches the current page, not the flat (arbitrary) top-level lobbyId
        const match = isOnRankedPath
          ? (res.data.ranked  || { inLobby: false, lobbyId: null })
          : (res.data.regular || { inLobby: false, lobbyId: null });
        if (match.inLobby && match.lobbyId) {
          navigate(`/team-battle/lobby/${match.lobbyId}`);
        } else {
          navigate(isOnRankedPath ? '/ranked-battle' : '/team-battle');
        }
      } catch {
        navigate(location.pathname.startsWith('/ranked-battle') ? '/ranked-battle' : '/team-battle');
      }
    } else if (location.pathname === '/beast-battle') {
      navigate('/beasts');
    }
  }, [mercenaryId, switchMercenary, location.pathname, navigate]);

  const handleReportIssue = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowIssueForm(true);
  };

  const handleCloseIssueForm = () => {
    setShowIssueForm(false);
  };

  const seasonIdRef = useRef<number | null>(null);

  const fetchOnlineMercenaries = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/online-count', { withCredentials: true });
      setOnlineMercenaries(response.data.count);
    } catch (error) {
      console.error('Error fetching online mercenaries:', error);
    }
  }, []);

  const fetchUsers24h = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/users-24h', { withCredentials: true });
      setUsers24h(response.data.count);
    } catch (error) {
      console.error('Error fetching 24h users:', error);
    }
  }, []);

  const fetchUsersSeason = useCallback(async () => {
    try {
      if (seasonIdRef.current) {
        const response = await axios.get(`/api/user/users-season/${seasonIdRef.current}`, { withCredentials: true });
        setUsersSeason(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching season users:', error);
      setUsersSeason(0);
    }
  }, []);

  const fetchBattlesToday = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/battles-today', { withCredentials: true });
      setBattlesToday(response.data.count);
    } catch (error) {
      console.error('Error fetching today battles:', error);
    }
  }, []);

  const fetchBattlesWeek = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/battles-week', { withCredentials: true });
      setBattlesWeek(response.data.count);
    } catch (error) {
      console.error('Error fetching week battles:', error);
    }
  }, []);

  const fetchBattlesSeason = useCallback(async () => {
    try {
      if (seasonIdRef.current) {
        const response = await axios.get(`/api/user/season-battles/${seasonIdRef.current}`, { withCredentials: true });
        setBattlesSeason(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching season battles:', error);
      setBattlesSeason(0);
    }
  }, []);

  const fetchSeasonInfo = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/current-season', { withCredentials: true });
      if (response.data.season) {
        setSeasonInfo(response.data.season);
        seasonIdRef.current = response.data.season.id;
      } else {
        setSeasonInfo(null);
        seasonIdRef.current = null;
      }
    } catch (error) {
      console.error('Error fetching season info:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      if (!document.hidden) {
        fetchOnlineMercenaries();
        fetchUsers24h();
        fetchBattlesToday();
        fetchBattlesWeek();
        fetchSeasonInfo();
      }
      // Update stats every 30 seconds, season info every 5 minutes
      const statsInterval = setInterval(() => {
        if (document.hidden) return;
        fetchOnlineMercenaries();
        fetchUsers24h();
        fetchBattlesToday();
        fetchBattlesWeek();
      }, 30000);
      const seasonInterval = setInterval(() => {
        if (document.hidden) return;
        fetchSeasonInfo();
      }, 300000);
      return () => {
        clearInterval(statsInterval);
        clearInterval(seasonInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Tick local clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      if (document.hidden) return;
      setLocalTime(new Date());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);

  // Compute server time offset once from the Date response header
  useEffect(() => {
    if (!user) return;
    const fetchServerOffset = async () => {
      try {
        const resp = await axios.get('/api/user/online-count', { withCredentials: true });
        const serverDateStr = resp.headers['date'];
        if (serverDateStr) {
          setServerTimeOffset(new Date(serverDateStr).getTime() - Date.now());
        }
      } catch {}
    };
    if (!document.hidden) {
      fetchServerOffset();
    }
  }, [user]);

  useEffect(() => {
    if (seasonInfo?.id) {
      seasonIdRef.current = seasonInfo.id;
      if (!document.hidden) {
        fetchUsersSeason();
        fetchBattlesSeason();
      }
      const interval = setInterval(() => {
        if (document.hidden) return;
        fetchUsersSeason();
        fetchBattlesSeason();
      }, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonInfo?.id]);

  const formatClock = (d: Date, opts?: Intl.DateTimeFormatOptions) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, ...opts });

  const serverTime = new Date(localTime.getTime() + serverTimeOffset);

  return (
    <>
      <header className={styles.banner}>
        <div className={styles.leftSection}>
          <Link to="/home">
            <h1 className={styles.logo}>Njordheim Arena</h1>
          </Link>
        </div>

        {/* Center group: stacked clocks + season card */}
        {user && (
          <div className={styles.centerGroup}>
            <div className={styles.clockStack}>
              <div className={styles.clockWidget}>
                <span className={styles.clockLabel}>Server (UTC)</span>
                <span className={styles.clockTime}>{formatClock(serverTime, { timeZone: 'UTC' })}</span>
              </div>
              <div className={styles.clockWidget}>
                <span className={styles.clockLabel}>Local</span>
                <span className={styles.clockTime}>{formatClock(localTime)}</span>
              </div>
            </div>

            <div
              className={`${styles.seasonInfo}${seasonInfo ? ` ${styles.seasonInfoClickable}` : ''}`}
              onClick={seasonInfo ? handleSeasonCardClick : undefined}
              title={seasonInfo ? 'Click to see season schedule' : undefined}
            >
            {seasonInfo ? (
              <>
                <span className={styles.seasonName}>{seasonInfo.name}</span>
                <span className={styles.seasonDay}>
                  Day {(seasonInfo.currentDayNumber ?? -1) + 1} / {seasonInfo.totalDays ?? 53}
                </span>
                {/* Row 1: Mercenaries */}
                <div className={styles.statsContainer}>
                  <div className={styles.rowLabel}>Mercs</div>
                  <div className={styles.statsRow}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Today:</span>
                      <span className={styles.statValue}>{onlineMercenaries}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Week:</span>
                      <span className={styles.statValue}>{users24h}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Season:</span>
                      <span className={styles.statValue}>{usersSeason}</span>
                    </div>
                  </div>
                </div>
                {/* Row 2: Battles */}
                <div className={styles.statsContainer}>
                  <div className={styles.rowLabel}>Battles</div>
                  <div className={styles.statsRow}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Today:</span>
                      <span className={styles.statValue}>{battlesToday}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Week:</span>
                      <span className={styles.statValue}>{battlesWeek}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Season:</span>
                      <span className={styles.statValue}>{battlesSeason}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <span className={styles.offseason}>Offseason</span>
              </>
            )}
            </div>
          </div>
        )}

        <div className={styles.rightGroup}>
          {/* Quick swap mercenary, desktop only */}
          {user && mercenaries.length > 0 && (
            <div className={styles.quickSwap}>
            {mercenaries.map(m => (
              <button
                key={m.id}
                className={`${styles.quickSwapSlot} ${m.id === mercenaryId ? styles.quickSwapActive : ''} ${m.id !== mercenaryId && (Number(m.primarySkillPoints) > 0 || Number(m.secondarySkillPoints) > 0) ? styles.quickSwapHasPoints : ''}`}
                onClick={() => handleQuickSwitch(m)}
                title={`${m.name} · Lvl ${m.level}${(Number(m.primarySkillPoints) > 0 || Number(m.secondarySkillPoints) > 0) ? ' · Unspent skill points!' : ''}`}
              >
                <img
                  src={`/img/avatars/${m.race?.toLowerCase() ?? 'default'}.${m.sex?.toLowerCase() ?? 'default'}.${m.avatar ?? 1}.png`}
                  alt={m.name}
                  className={styles.quickSwapAvatar}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/img/avatars/human.male.1.png'; }}
                />
              </button>
            ))}
            </div>
          )}

          <div className={styles.topRightLinks}>
            <button
              className={styles.issueButton}
              onClick={handleReportIssue}
            >
              Report an Issue
            </button>
            {pendingFeedback && onOpenFeedback && (
              <button
                className={styles.issueButton}
                onClick={onOpenFeedback}
              >
                Give Feedback
              </button>
            )}
            {user?.role?.name === 'Admin' || user?.role?.name === 'Owner' ? (
              <Link to="/admin">Admin Page</Link>
            ) : null}
            {user?.role?.name === 'GameMaster' || user?.role?.name === 'Admin' || user?.role?.name === 'Owner' ? (
              <Link to="/gamemaster">Game Master Page</Link>
            ) : null}
          </div>
        </div>
      </header>

      {showIssueForm && (
        <IssueReportForm
          onClose={handleCloseIssueForm}
          onSubmit={handleCloseIssueForm}
        />
      )}

      {showScheduleModal && (
        <div className={styles.scheduleOverlay} onClick={() => setShowScheduleModal(false)}>
          <div className={styles.scheduleModal} onClick={e => e.stopPropagation()}>
            <div className={styles.scheduleModalHeader}>
              <h3 className={styles.scheduleModalTitle}>
                {seasonInfo?.name}, Season Schedule
              </h3>
              <button className={styles.scheduleModalClose} onClick={() => setShowScheduleModal(false)}>✕</button>
            </div>
            <div className={styles.scheduleModalBody}>
              {scheduleLoading ? (
                <p className={styles.scheduleLoading}>Loading schedule…</p>
              ) : scheduleEvents.length === 0 ? (
                <p className={styles.scheduleEmpty}>No upcoming events.</p>
              ) : (
                <table className={styles.scheduleTable}>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Scheduled</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleEvents.map(ev => (
                      <tr key={ev.id} className={ev.status === 'in_progress' ? styles.scheduleRowActive : ''}>
                        <td>{ev.eventName}</td>
                        <td>{new Date(ev.scheduledDate).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td>
                          <span className={`${styles.scheduleStatusBadge} ${ev.status === 'in_progress' ? styles.badgeActive : styles.badgeScheduled}`}>
                            {ev.status === 'in_progress' ? 'IN PROGRESS' : 'SCHEDULED'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
